import React from 'react'
import type { ReactNode } from 'react'
import { Loader } from '@progress/kendo-react-indicators'
import KMK_LOADING from '@public/assets/common/kmk_loading.gif'
import type {
  NarrativeOcgValidationAction,
  NarrativeOcgValidationHint,
} from '../types'
import { getEditorClassName } from '../utils/classNames'
import { getNarrativeSeverity } from '../utils/issueDetails'
import { isNarrativeOcgFeatureEnabled } from '../utils/isNarrativeOcgFeatureEnabled'
import { NarrativeOcgValidationBars } from './NarrativeOcgValidationBars'
import { NarrativeOcgValidationPanel } from './NarrativeOcgValidationPanel'
import './narrativeOcg.css'

type NarrativeOcgEditorProps = {
  children: ReactNode
  footerPanel?: ReactNode
  isMobile?: boolean
  toolbar?: ReactNode
  toolbarStart?: ReactNode
  toolbarEnd?: ReactNode
  validationHint?: NarrativeOcgValidationHint
  onAcceptValidation?: (suggestedNarrative: string) => void
  onDiscardValidation?: () => void
  onRevertValidation?: () => void
  onRewriteValidation?: () => void
  validationActionLoading?: NarrativeOcgValidationAction | null
  suggestionLoading?: boolean
  onFloatingWindowInteraction?: (isOpen?: boolean) => void
  dialogActions?: boolean
}

export const NarrativeOcgEditor = ({
  children,
  footerPanel,
  isMobile = false,
  toolbar,
  toolbarStart,
  toolbarEnd,
  validationHint,
  onAcceptValidation,
  onDiscardValidation,
  onRevertValidation,
  onRewriteValidation,
  validationActionLoading,
  suggestionLoading = false,
  onFloatingWindowInteraction,
  dialogActions = false,
}: NarrativeOcgEditorProps) => {
  const severity = getNarrativeSeverity(validationHint)
  const isChecking = Boolean(validationHint?.isLoading)
  const isOcgValidation = isNarrativeOcgFeatureEnabled()
  const hasToolbarSlots = Boolean(toolbarStart || toolbarEnd)

  return (
    <div className={getEditorClassName(severity, isMobile)}>
      <div
        className="narrative-ocg-editor__field m-3 mb-0 min-h-[180px] rounded bg-white p-2.5"
      >
        {children}
      </div>

      {toolbar && (
        <div className="narrative-ocg-editor__toolbar px-3 pb-1 pt-0">
          {toolbar}
        </div>
      )}
      {!toolbar && hasToolbarSlots && (
        <div className="narrative-ocg-editor__toolbar px-3 pb-1 pt-0">
          <div className="narrative-ocg-editor__toolbar-row flex h-9 items-center justify-between px-2 py-1 max-[640px]:h-auto max-[640px]:min-h-9 max-[640px]:items-start">
            <div className="narrative-ocg-editor__toolbar-left inline-flex min-w-0 items-center gap-4 max-[640px]:flex-wrap max-[640px]:items-start max-[640px]:gap-2">
              {toolbarStart}
            </div>
            {toolbarEnd ? (
              <div className="narrative-ocg-editor__toolbar-right inline-flex items-center">
                {toolbarEnd}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {footerPanel}

      {/* MIRA-4330: keep dialog findings directly above MIRA Suggestion in both editor layouts. */}
      {dialogActions && (
        <NarrativeOcgValidationBars validationHint={validationHint} />
      )}
      <NarrativeOcgValidationPanel
        validationHint={validationHint}
        onAcceptValidation={onAcceptValidation}
        onDiscardValidation={onDiscardValidation}
        onRevertValidation={onRevertValidation}
        onRewriteValidation={onRewriteValidation}
        actionLoading={validationActionLoading}
        suggestionLoading={suggestionLoading}
        onFloatingWindowInteraction={onFloatingWindowInteraction}
        isMobile={isMobile}
        dialogActions={dialogActions}
      />
      {isChecking ? (
        <div
          aria-label="Checking narrative"
          className="narrative-ocg-editor__loading"
          role="status"
        >
          {isOcgValidation ? (
            <>
              <img alt="" src={KMK_LOADING} />
              <span className="narrative-ocg-editor__loading-text">
                Validating...
              </span>
            </>
          ) : (
            <Loader type="pulsing" size="medium" themeColor="secondary" />
          )}
        </div>
      ) : null}
    </div>
  )
}
